import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRecorder, isTabAudioCaptureSupported, type RecordSource } from "@/hooks/useRecorder";
import { useQuota } from "@/hooks/useQuota";
import { useMeetingStore } from "@/stores/meetingStore";
import { TemplatePicker } from "@/components/TemplatePicker";
import { CalendarPicker } from "@/components/CalendarPicker";
import type { TemplateKey, DetailLevel } from "@/types";

const MAX_FILE_SIZE = 24 * 1024 * 1024; // 24MB

export function Record() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status, formattedDuration, durationMs, level, start, pause, resume, stop, error: recorderError } = useRecorder();
  const { canRecord, meetingsLeft } = useQuota();
  const { createMeeting, processMeeting } = useMeetingStore();

  const [templateKey, setTemplateKey] = useState<TemplateKey>("default");
  const [detailLevel, setDetailLevel] = useState<DetailLevel>("detailed");
  const [context, setContext] = useState("");
  const [prefilledTitle, setPrefilledTitle] = useState<string | null>(null);
  const [enableDiarization, setEnableDiarization] = useState(false);
  const [recordSource, setRecordSource] = useState<RecordSource>("mic");
  const tabSupported = isTabAudioCaptureSupported();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleStop = async () => {
    setError(null);
    setProcessing(true);
    try {
      const { blob, mimeType } = await stop();
      const durationSec = Math.round(durationMs / 1000);

      const meeting = await createMeeting({
        title: prefilledTitle ?? undefined,
        template_key: templateKey,
        detail_level: detailLevel,
        context: context || undefined,
        duration_seconds: durationSec,
        enable_diarization: enableDiarization,
      });

      processMeeting(
        meeting.id,
        blob,
        mimeType,
        templateKey,
        context || undefined,
        detailLevel
      );

      navigate(`/meeting/${meeting.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
      setProcessing(false);
    }
  };

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);

      if (file.size > MAX_FILE_SIZE) {
        setError(t("record.fileTooLong"));
        return;
      }

      setProcessing(true);
      try {
        const meeting = await createMeeting({
          title: file.name.replace(/\.[^.]+$/, ""),
          template_key: templateKey,
          detail_level: detailLevel,
          context: context || undefined,
          enable_diarization: enableDiarization,
        });

        processMeeting(
          meeting.id,
          file,
          file.type || "audio/mpeg",
          templateKey,
          context || undefined,
          detailLevel
        );

        navigate(`/meeting/${meeting.id}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.error"));
        setProcessing(false);
      }
    },
    [templateKey, detailLevel, context, createMeeting, processMeeting, navigate, t]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const isRecording = status === "recording";
  const isPaused = status === "paused";
  const isActive = isRecording || isPaused;

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-xl font-bold text-redi-text mb-6">
        {t("tabs.record")}
      </h2>

      {/* Recording UI */}
      {isActive ? (
        <div className="flex flex-col items-center py-16">
          <div className="text-5xl font-mono text-redi-text mb-4">
            {formattedDuration}
          </div>
          <div className="w-48 h-2 bg-redi-surface rounded-full overflow-hidden mb-8">
            <div
              className="h-full bg-redi-accent transition-[width] duration-75"
              style={{
                width: `${Math.round((isPaused ? 0 : level) * 100)}%`,
              }}
            />
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={isPaused ? resume : pause}
              disabled={processing}
              className="w-14 h-14 rounded-full bg-redi-surface border border-white/10 flex items-center justify-center hover:bg-redi-surface/80 transition-colors disabled:opacity-50"
              aria-label={isPaused ? t("record.resume") : t("record.pause")}
            >
              {isPaused ? (
                <svg className="w-6 h-6" fill="white" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="white" viewBox="0 0 24 24">
                  <rect x="6" y="5" width="4" height="14" rx="1" />
                  <rect x="14" y="5" width="4" height="14" rx="1" />
                </svg>
              )}
            </button>
            <button
              onClick={handleStop}
              disabled={processing}
              className="w-20 h-20 rounded-full bg-redi-accent flex items-center justify-center hover:bg-redi-accent/80 transition-colors disabled:opacity-50"
              aria-label={t("record.tapToStop")}
            >
              <svg className="w-8 h-8" fill="white" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          </div>
          <p className="text-redi-muted text-sm mt-4">
            {isPaused ? t("record.paused") : t("record.recording")}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <CalendarPicker
            onPick={({ title, context: ctx }) => {
              setPrefilledTitle(title);
              if (ctx) setContext(ctx);
            }}
          />
          {prefilledTitle && (
            <div className="bg-redi-primary/10 border border-redi-primary/30 rounded-lg px-3 py-2 text-sm text-redi-text">
              {t("calendar.prefilled")}: <strong>{prefilledTitle}</strong>{" "}
              <button
                onClick={() => setPrefilledTitle(null)}
                className="text-redi-muted hover:text-redi-text ml-2"
              >
                ×
              </button>
            </div>
          )}

          {/* Template picker */}
          <TemplatePicker selected={templateKey} onChange={setTemplateKey} />

          {/* Detail level */}
          <div>
            <label className="text-sm font-medium text-redi-text mb-2 block">
              {t("record.detailLevel")}
            </label>
            <div className="flex gap-2">
              {(["concise", "detailed"] as DetailLevel[]).map((level) => (
                <button
                  key={level}
                  onClick={() => setDetailLevel(level)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    detailLevel === level
                      ? "bg-redi-primary text-white"
                      : "bg-redi-surface text-redi-muted hover:text-redi-text"
                  }`}
                >
                  {t(`record.detail_${level}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Diarization toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-redi-text">
                {t("record.diarizationLabel")}
              </p>
              <p className="text-xs text-redi-muted mt-0.5">
                {t("record.diarizationHint")}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setEnableDiarization((v) => !v)}
              className={`w-12 h-6 rounded-full relative transition-colors ${
                enableDiarization ? "bg-redi-primary" : "bg-redi-surface"
              }`}
              aria-pressed={enableDiarization}
            >
              <span
                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                  enableDiarization ? "left-6" : "left-0.5"
                }`}
              />
            </button>
          </div>

          {/* Context */}
          <div>
            <label className="text-sm font-medium text-redi-text mb-2 block">
              {t("record.contextLabel")}
            </label>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder={t("record.contextPlaceholder")}
              maxLength={500}
              rows={3}
              className="w-full px-4 py-3 bg-redi-surface border border-white/5 rounded-lg text-redi-text placeholder-redi-muted focus:outline-none focus:border-redi-primary resize-none"
            />
            <p className="text-xs text-redi-muted mt-1 text-right">
              {context.length}/500
            </p>
          </div>

          {tabSupported && (
            <div>
              <label className="text-sm font-medium text-redi-text mb-2 block">
                {t("record.sourceLabel")}
              </label>
              <div className="flex flex-wrap gap-2">
                {(["mic", "tab+mic", "tab"] as RecordSource[]).map((src) => (
                  <button
                    key={src}
                    type="button"
                    onClick={() => setRecordSource(src)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                      recordSource === src
                        ? "bg-redi-primary text-white"
                        : "bg-redi-surface text-redi-muted hover:text-redi-text"
                    }`}
                  >
                    {t(`record.source_${src.replace("+", "_")}`)}
                  </button>
                ))}
              </div>
              {recordSource !== "mic" && (
                <p className="text-redi-muted text-xs mt-2">
                  {t("record.tabHint")}
                </p>
              )}
            </div>
          )}

          {/* Record button */}
          <div className="flex flex-col items-center py-8">
            <button
              onClick={() => start(recordSource)}
              disabled={!canRecord || processing}
              className={`w-20 h-20 rounded-full flex items-center justify-center transition-colors ${
                canRecord
                  ? "bg-redi-primary hover:bg-redi-primary/80"
                  : "bg-redi-muted/30 cursor-not-allowed"
              }`}
            >
              <svg className="w-8 h-8" fill="white" viewBox="0 0 24 24">
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
              </svg>
            </button>

            {!canRecord && (
              <p className="text-redi-warning text-sm mt-4">
                {t("record.limitReached")}
              </p>
            )}

            {canRecord && meetingsLeft !== Infinity && (
              <p className="text-redi-muted text-sm mt-4">
                {t("record.meetingsLeft", { count: meetingsLeft })}
              </p>
            )}
          </div>

          {/* Drag & drop / file import */}
          <div
            ref={dropRef}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
              dragOver
                ? "border-redi-primary bg-redi-primary/5"
                : "border-white/10 hover:border-white/20"
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <p className="text-redi-muted text-sm">
              {t("record.dropOrClick")}
            </p>
            <p className="text-redi-muted/50 text-xs mt-1">
              MP3, M4A, WAV, MP4, WebM... (max 24 MB)
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,video/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
                e.target.value = "";
              }}
            />
          </div>
        </div>
      )}

      {(recorderError || error) && (
        <p className="text-redi-error text-sm text-center mt-4">
          {recorderError || error}
        </p>
      )}
    </div>
  );
}
